import React, { useMemo, useState } from 'react';
import {
  Check,
  CheckCircle2,
  FileText,
  Loader2,
  Monitor,
  Network,
  Package,
  Shield,
  UserCog,
  Warehouse,
  X
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Combobox } from '../../components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog';
import { SearchableSelect, SearchableSelectGroup } from '../../components/ui/searchable-select';
import { cn } from '../../components/ui/utils';
import {
  ArchiveCategory,
  ArchiveCategoryId,
  ArchiveDependencyItem,
  ArchiveEvent,
  ArchiveReplacement,
  PartyType
} from '../../types/personArchive';

const CATEGORY_ICONS: Record<ArchiveCategoryId, React.ElementType> = {
  assets: Package,
  business_systems: Monitor,
  owned_procedures: FileText,
  manager_of: UserCog,
  raci: Network,
  service_desk_admin: Shield,
  yard_manager: Warehouse
};

const EVENT_TIME = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'Australia/Sydney'
});

export function formatEventTime(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return EVENT_TIME.format(parsed);
}

export interface JourneyPickerOption {
  value: string;
  label: string;
  subtitle?: string;
}

export interface JourneyPickerConfig {
  users: JourneyPickerOption[];
  raciGroups: SearchableSelectGroup[];
  deskAdmins: JourneyPickerOption[];
}

/** Selection can span categories, so keys are namespaced by category id. */
function selectionKey(categoryId: ArchiveCategoryId, itemId: string) {
  return `${categoryId}:${itemId}`;
}

/**
 * Turn a picker value into a replacement. Shared by the per-row picker and the
 * bulk bar so the two can never decode `JobRole-12` differently.
 */
function decodeReplacement(
  categoryId: ArchiveCategoryId,
  value: string,
  pickerConfig: JourneyPickerConfig
): ArchiveReplacement {
  if (categoryId === 'raci') {
    const [type, ...idParts] = value.split('-');
    const option = pickerConfig.raciGroups
      .flatMap((group) => group.options)
      .find((candidate) => candidate.value === value);
    return { id: idParts.join('-'), name: option ? option.label : '', type: type as PartyType };
  }

  const option = (categoryId === 'service_desk_admin' ? pickerConfig.deskAdmins : pickerConfig.users).find(
    (candidate) => candidate.value === value
  );
  return { id: value, name: option ? option.label : '', type: 'User' };
}

interface ReplacementPickerProps {
  categoryId: ArchiveCategoryId;
  pickerConfig: JourneyPickerConfig;
  value: string;
  onChange: (value: string) => void;
}

const ReplacementPicker: React.FC<ReplacementPickerProps> = ({ categoryId, pickerConfig, value, onChange }) => (
  <div className="min-w-[220px] flex-1 md:max-w-[320px]">
    {categoryId === 'raci' ? (
      <SearchableSelect
        groups={pickerConfig.raciGroups}
        value={value}
        onChange={onChange}
        placeholder="Search people…"
      />
    ) : (
      <Combobox
        options={categoryId === 'service_desk_admin' ? pickerConfig.deskAdmins : pickerConfig.users}
        value={value}
        onChange={(next) => onChange(next || '')}
        placeholder="Search people…"
        emptyMessage={
          categoryId === 'service_desk_admin' ? 'Nobody holds the desk administrator permission.' : 'No people found.'
        }
      />
    )}
  </div>
);

interface ArchiveDependenciesProps {
  categories: ArchiveCategory[];
  events: ArchiveEvent[];
  activeId: ArchiveCategoryId | null;
  onSelect: (id: ArchiveCategoryId) => void;
  pickerConfig: JourneyPickerConfig;
  onResolve: (
    category: ArchiveCategoryId,
    item: ArchiveDependencyItem,
    replacement?: ArchiveReplacement
  ) => Promise<void>;
  /** Resolve many rows with one replacement (or none, for unassign/remove). */
  onBulkResolve: (
    category: ArchiveCategoryId,
    items: ArchiveDependencyItem[],
    replacement?: ArchiveReplacement
  ) => Promise<void>;
  /** Non-null while a bulk run is in flight. */
  bulkProgress: { done: number; total: number } | null;
  busy: boolean;
}

/**
 * The Dependencies section: the category rail plus the panel for the selected
 * category (rows, hand-over pickers, and the change history once it is empty).
 *
 * Only Responsible/Accountable RACI rows appear here — the same rows the archive
 * blockers count, so resolving everything in this panel really does unblock
 * archiving.
 */
export const ArchiveDependencies: React.FC<ArchiveDependenciesProps> = ({
  categories,
  events,
  activeId,
  onSelect,
  pickerConfig,
  onResolve,
  onBulkResolve,
  bulkProgress,
  busy
}) => {
  const [pickValue, setPickValue] = useState('');
  const [resolvingItemId, setResolvingItemId] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  /** Which hand-over the modal is for: one row, or every selected row. */
  const [pickerTarget, setPickerTarget] = useState<
    { mode: 'single'; item: ArchiveDependencyItem } | { mode: 'bulk' } | null
  >(null);

  const active = useMemo(
    () => categories.find((category) => category.id === activeId) || categories[0],
    [categories, activeId]
  );

  const activeEvents = useMemo(
    () => (active ? events.filter((event) => event.category === active.id) : []),
    [events, active]
  );

  const selectedItems = useMemo(
    () => (active ? active.items.filter((item) => selectedKeys.has(selectionKey(active.id, item.id))) : []),
    [active, selectedKeys]
  );

  const totalToResolve = categories.reduce((total, category) => total + category.count, 0);
  const categoriesToResolve = categories.filter((category) => !category.resolved).length;

  const toggleItem = (item: ArchiveDependencyItem) => {
    if (!active) return;
    const key = selectionKey(active.id, item.id);
    setSelectedKeys((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (!active) return;
    setSelectedKeys((previous) => {
      const allSelected = active.items.every((item) => previous.has(selectionKey(active.id, item.id)));
      if (allSelected) return new Set();
      return new Set(active.items.map((item) => selectionKey(active.id, item.id)));
    });
  };

  const clearSelection = () => {
    if (!active) return;
    setSelectedKeys((previous) => {
      const next = new Set(previous);
      active.items.forEach((item) => next.delete(selectionKey(active.id, item.id)));
      return next;
    });
  };

  const openItemPicker = (item: ArchiveDependencyItem) => {
    setPickValue('');
    setPickerTarget({ mode: 'single', item });
  };

  const openBulkPicker = () => {
    if (selectedItems.length === 0) return;
    setPickValue('');
    setPickerTarget({ mode: 'bulk' });
  };

  const closePicker = () => {
    setPickerTarget(null);
    setPickValue('');
  };

  /** Confirm the modal: one row, or every selected row with the same replacement. */
  const confirmPicker = async () => {
    if (!active || !pickerTarget || !pickValue) return;

    const target = pickerTarget;
    const replacement = decodeReplacement(active.id, pickValue, pickerConfig);
    closePicker();

    if (target.mode === 'single') {
      setResolvingItemId(target.item.id);
      try {
        await onResolve(active.id, target.item, replacement);
      } finally {
        setResolvingItemId(null);
      }
      return;
    }

    await onBulkResolve(active.id, selectedItems, replacement);
    clearSelection();
  };

  /** Unassign/remove need no replacement, so those categories act straight away. */
  const runBulkWithoutPicker = async () => {
    if (!active || selectedItems.length === 0) return;
    await onBulkResolve(active.id, selectedItems);
    clearSelection();
  };

  const resolveWithoutPicker = async (item: ArchiveDependencyItem) => {
    if (!active) return;
    setResolvingItemId(item.id);
    try {
      await onResolve(active.id, item);
    } finally {
      setResolvingItemId(null);
    }
  };

  if (!active) return null;

  const ActiveIcon = CATEGORY_ICONS[active.id];
  const allSelected = active.items.length > 0 && selectedItems.length === active.items.length;
  const someSelected = selectedItems.length > 0 && !allSelected;
  const panelBusy = busy || !!bulkProgress;
  const rowActionLabel =
    active.action === 'transfer'
      ? 'Transfer Ownership'
      : active.id === 'manager_of'
      ? 'Reassign Manager'
      : active.id === 'service_desk_admin'
      ? 'Reassign Admin'
      : active.id === 'yard_manager'
      ? 'Reassign Yard Manager'
      : 'Reassign';
  const bulkLabel = active.picker
    ? 'Bulk Reassign'
    : active.id === 'assets'
    ? 'Return selected'
    : 'Remove selected';
  const pickerTargetItem = pickerTarget && pickerTarget.mode === 'single' ? pickerTarget.item : null;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Dependencies</h2>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Everything assigned to them must be unassigned or handed over. These changes take effect immediately.
          </p>
        </div>
        <div className={cn('whitespace-nowrap text-[13px] font-medium', totalToResolve > 0 ? 'text-amber-700' : 'text-green-600')}>
          {totalToResolve > 0
            ? `${totalToResolve} ${totalToResolve === 1 ? 'item' : 'items'} to resolve in ${categoriesToResolve} ${
                categoriesToResolve === 1 ? 'category' : 'categories'
              }`
            : 'All categories resolved'}
        </div>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        {/* Category rail — wraps into a pill row on small screens */}
        <div className="flex flex-wrap gap-1.5 rounded-xl border bg-card p-2 lg:w-[276px] lg:flex-shrink-0 lg:flex-col lg:flex-nowrap">
          {categories.map((category) => {
            const Icon = CATEGORY_ICONS[category.id];
            const selected = category.id === active.id;
            return (
              <button
                key={category.id}
                type="button"
                onClick={() => {
                  closePicker();
                  onSelect(category.id);
                }}
                className={cn(
                  'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
                  selected ? 'bg-blue-50 text-blue-700' : 'text-foreground/80 hover:bg-accent'
                )}
              >
                <Icon size={18} className="flex-shrink-0 opacity-85" />
                <span className={cn('min-w-0 flex-1 truncate text-[13px]', selected && 'font-medium')}>{category.label}</span>
                {category.resolved ? (
                  <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-lg border border-green-300 bg-green-50 px-1.5 py-0.5 text-[11px] font-medium text-green-700">
                    <Check size={11} strokeWidth={3} />
                    Resolved
                  </span>
                ) : (
                  <span className="flex-shrink-0 rounded-lg border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
                    {category.count} to resolve
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Detail panel */}
        <div className="min-w-0 flex-1 overflow-hidden rounded-xl border bg-card">
          <div className="flex items-center justify-between gap-3 border-b px-5 py-4">
            <div className="flex items-center gap-2.5">
              <ActiveIcon size={18} className="text-muted-foreground" />
              <div>
                <div className="text-sm font-semibold">{active.label}</div>
                <div className="text-xs text-muted-foreground">{active.subtitle}</div>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2.5">
              {bulkProgress && (
                <span className="flex items-center gap-1.5 text-xs font-medium text-amber-700">
                  <Loader2 size={13} className="animate-spin" />
                  {bulkProgress.done} / {bulkProgress.total}
                </span>
              )}

              {selectedItems.length > 0 && !bulkProgress && (
                <span className="text-xs font-medium text-blue-700">{selectedItems.length} selected</span>
              )}

              {active.count > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1.5"
                  disabled={panelBusy || selectedItems.length === 0}
                  onClick={active.picker ? openBulkPicker : runBulkWithoutPicker}
                  title={selectedItems.length === 0 ? 'Select rows first' : bulkLabel}
                >
                  <Network size={14} />
                  {selectedItems.length > 1 ? `${bulkLabel} (${selectedItems.length})` : bulkLabel}
                </Button>
              )}

              <span className="text-xs text-muted-foreground">
                {active.count > 0 ? `${active.count} ${active.count === 1 ? 'item' : 'items'} to resolve` : 'Resolved'}
              </span>
            </div>
          </div>

          {active.count > 0 && (
            <div>
              <div className="hidden gap-3 border-b bg-slate-50 px-5 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground md:flex">
                <div className="flex w-8 flex-shrink-0 items-center">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                    disabled={panelBusy}
                    onCheckedChange={toggleAll}
                    aria-label="Select all rows in this category"
                  />
                </div>
                <div className="flex-[2] min-w-0">{active.columns[0]}</div>
                <div className="flex-[1.6] min-w-0">{active.columns[1]}</div>
                <div className="flex-1 min-w-0">{active.columns[2]}</div>
                <div className="w-[168px] flex-shrink-0" />
              </div>

              {active.items.map((item) => (
                <div key={item.id} className="border-b last:border-b-0">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-3 md:flex-nowrap">
                    <div className="flex w-8 flex-shrink-0 items-center">
                      <Checkbox
                        checked={selectedKeys.has(selectionKey(active.id, item.id))}
                        disabled={panelBusy}
                        onCheckedChange={() => toggleItem(item)}
                        aria-label={`Select ${item.values[0]}`}
                      />
                    </div>
                    {item.values.map((value, index) => (
                      <div
                        key={index}
                        className={cn(
                          'min-w-0 md:flex-[2]',
                          index === 1 && 'md:flex-[1.6] text-[13px] text-muted-foreground',
                          index === 2 && 'md:flex-1 text-[13px] text-muted-foreground',
                          index === 0 && 'w-full text-[13px] font-medium md:w-auto'
                        )}
                      >
                        <span className="mr-1 text-[11px] uppercase tracking-wide text-muted-foreground md:hidden">
                          {active.columns[index]}:
                        </span>
                        <span className="break-words">
                          {active.id === 'assets' && index === 0 && item.meta.assetId != null ? (
                            <Link
                              to={`/assets/${item.meta.assetId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                            >
                              {value}
                            </Link>
                          ) : (
                            value
                          )}
                        </span>
                      </div>
                    ))}

                    <div className="ml-auto flex flex-shrink-0 justify-end">
                      {!active.picker && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 gap-1.5"
                          disabled={panelBusy || resolvingItemId === item.id}
                          onClick={() => resolveWithoutPicker(item)}
                        >
                          {resolvingItemId === item.id ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
                          {active.id === 'assets' ? 'Return Asset' : 'Remove from System'}
                        </Button>
                      )}

                      {active.picker && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 gap-1.5"
                          disabled={panelBusy}
                          onClick={() => openItemPicker(item)}
                        >
                          <Network size={14} />
                          {rowActionLabel}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {active.count === 0 && (
            <div>
              <div className="flex flex-col items-center gap-2 px-5 py-9 text-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-50 text-green-600">
                  <CheckCircle2 size={20} />
                </div>
                <div className="text-sm font-semibold">Nothing to resolve</div>
                <div className="max-w-md text-[13px] text-muted-foreground">{active.empty}</div>
              </div>

              <div className="border-t px-5 py-4">
                <div className="mb-3 text-[11px] uppercase tracking-wider text-muted-foreground">Change history</div>
                {activeEvents.length > 0 ? (
                  <div className="flex flex-col gap-3.5">
                    {activeEvents.map((event) => (
                      <div key={event.id} className="flex gap-3">
                        <div className="flex flex-[0_0_8px] flex-col items-center pt-1.5">
                          <span className="h-2 w-2 rounded-full bg-green-500" />
                          <span className="mt-1 w-px flex-1 bg-border" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-[13px]">{event.text}</div>
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {event.actor ? event.actor.name : 'Unknown'} — {formatEventTime(event.createdAt)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-[13px] text-muted-foreground">No recorded changes for this category.</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hand-over modal — one row, or every selected row */}
      <Dialog
        open={!!pickerTarget}
        onOpenChange={(open) => {
          if (!open) closePicker();
        }}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">
              {pickerTargetItem ? rowActionLabel : bulkLabel}
            </DialogTitle>
            <DialogDescription className="text-sm">
              {pickerTargetItem
                ? `Hand this over to someone else. The change takes effect immediately.`
                : `${selectedItems.length} selected in ${active.label}. One replacement is applied to every selected row — anything that cannot be handed over stays in the panel.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {pickerTargetItem ? (
              <div className="rounded-lg border bg-slate-50 px-3.5 py-3">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {active.columns[0]}
                </div>
                <div className="mt-0.5 text-[13px] font-medium">{pickerTargetItem.values[0]}</div>
                {pickerTargetItem.values[1] && (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {active.columns[1]}: {pickerTargetItem.values[1]}
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-lg border border-blue-200 bg-blue-50/60 px-3.5 py-3">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{active.label}</div>
                <div className="mt-0.5 text-[13px] font-medium">
                  {selectedItems.length} {selectedItems.length === 1 ? 'item' : 'items'} selected
                </div>
              </div>
            )}

            <div>
              <div className="mb-1.5 text-[13px] font-medium">{active.pickerLabel}</div>
              <ReplacementPicker
                categoryId={active.id}
                pickerConfig={pickerConfig}
                value={pickValue}
                onChange={setPickValue}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closePicker}>
              Cancel
            </Button>
            <Button disabled={!pickValue || panelBusy} onClick={confirmPicker} className="gap-2">
              {resolvingItemId ? <Loader2 size={14} className="animate-spin" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
